import React from "react";
import styled from "styled-components";

import Tooltip from "@mui/material/Tooltip";
import { TextEntryFormControl } from "../TextEntryFormControl";
import { TextControlStyle } from "../../Utilities/FormDesignConstants";
import { StyledFormHelperText, StyledTextField } from "./CommonStyled";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { PropertyEditorInteger } from "../../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

class XMultiLineTextEditEditUI extends React.Component<{ editMe: MultiLineTextEditFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}

                <PropertyEditorInteger
                    editObject={this.props.editMe}
                    propertyName="rowMax"
                    label="Rows Max"
                />

                <PropertyEditorInteger
                    editObject={this.props.editMe}
                    propertyName="rowMin"
                    label="Rows Min"
                />

                <PropertyEditorInteger
                    editObject={this.props.editMe}
                    propertyName="min"
                    label="Min Length"
                />
                <PropertyEditorInteger
                    editObject={this.props.editMe}
                    propertyName="max"
                    label="Max Length"
                />

                {this.props.editMe.renderTextControlBasePropertyEditor()}
                {this.props.editMe.renderTextStyleSelectionEditor()}
            </UISpacedBoxContent>
        );
    }
}

export class MultiLineTextEditFormControl extends TextEntryFormControl {
    readonly __type: string = "Text Area";

    rowMax: number = 10;
    rowMin: number = 5;
    min?: number;
    max?: number;

    render() {
        let style = this.getFormContext()?.form?.defaultTextStyle;

        if (this.overrideStyle && this.style) style = this.style;

        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";
        const customWidth = this.fullWidth ? "100%" : this.width ? `${this.width}px` : "50%";

        switch (style) {
            case TextControlStyle.LABELED:
                return (
                    <StyledTextFieldWrap width={customWidth}>
                        {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                        <StyledTextField
                            width={customWidth}
                            variant={"outlined"}
                            fullWidth={this.fullWidth}
                            error={Boolean(issueText)}
                            data-testid={this.valueName}
                            rows={this.rowMin}
                            rowsMax={this.rowMax}
                            multiline
                            onChange={this.handleChange}
                            value={this.valueName ? this.getFormDataValue(this.valueName) : ""}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "textarea"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "textarea"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
            case TextControlStyle.SHADED:
                return (
                    <StyledTextFieldWrap width={customWidth}>
                        <StyledTextField
                            width={customWidth}
                            variant={"filled"}
                            customstyle={"filled"}
                            fullWidth={this.fullWidth}
                            error={Boolean(issueText)}
                            data-testid={this.valueName}
                            rows={this.rowMin}
                            rowsMax={this.rowMax}
                            multiline
                            onChange={this.handleChange}
                            value={this.valueName ? this.getFormDataValue(this.valueName) : ""}
                            label={this.label}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "textarea"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "textarea"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
            case TextControlStyle.UNDERLINED:
                return (
                    <StyledTextFieldWrap width={customWidth}>
                        <StyledTextField
                            width={customWidth}
                            variant={"standard"}
                            customstyle={"standard"}
                            fullWidth={this.fullWidth}
                            error={Boolean(issueText)}
                            data-testid={this.valueName}
                            rows={this.rowMin}
                            rowsMax={this.rowMax}
                            multiline
                            onChange={this.handleChange}
                            value={this.valueName ? this.getFormDataValue(this.valueName) : ""}
                            label={this.label}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "textarea"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "textarea"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
            case TextControlStyle.OUTLINE:
                return (
                    <StyledTextFieldWrap width={customWidth}>
                        <StyledTextField
                            width={customWidth}
                            variant={"outlined"}
                            customstyle={"outlined"}
                            fullWidth={this.fullWidth}
                            error={Boolean(issueText)}
                            data-testid={this.valueName}
                            rows={this.rowMin}
                            rowsMax={this.rowMax}
                            multiline
                            onChange={this.handleChange}
                            value={this.valueName ? this.getFormDataValue(this.valueName) : ""}
                            label={this.label}
                            InputProps={{
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "textarea"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "textarea"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledTextFieldWrap>
                );
        }
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        const runtimeIssues = super.getRuntimeValidationIssues();
        const validationIssues = this.validate();
        if (validationIssues) {
            for (let val of validationIssues) {
                runtimeIssues.push(new ValidationIssue(val, this.valueName, this.id));
            }
        }

        return runtimeIssues;
    }

    renderEditUI(): JSX.Element | null {
        return <XMultiLineTextEditEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.name = this.valueName;
        return def;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type", "rowMax", "rowMin", "min", "max");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type", "rowMax", "rowMin", "min", "max");
    }

    private handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (this.valueName) {
            this.setFormDataValue(this.valueName, event.currentTarget.value);
        }
    };

    private validate = () => {
        let value = this.valueName ? this.getFormDataValue(this.valueName) : "";
        if (this.valueName && (this.min !== undefined || this.max !== undefined)) {
            const errorMessages: string[] = [];

            if (value && this.min && this.min > value.length) {
                errorMessages.push(`Value should be longer than ${this.min}`);
            }

            if (value && this.max && this.max < value.length) {
                errorMessages.push(`Value should be more short than ${this.max}`);
            }

            return errorMessages;
        }
    };
}

const StyledTextFieldWrap = styled.div<{ width?: string }>`
    width: ${({ width }) => width} !important;
    height: 100% !important;
    box-sizing: border-box !important;
    position: relative !important;
`;
